import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  // reorderCategories, // Reserved for future drag & drop functionality
  seedDefaultCategories,
  Category
} from '../../firebase/categoryService';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { auth } from '../../firebase/config';

const CategoriesEditor: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    isActive: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await getAllCategories();
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      isActive: true
    });
    setIsModalOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#3B82F6',
      isActive: category.isActive
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        // Update existing category
        await updateCategory(editingCategory.id!, formData);
      } else {
        // Create new category
        const nextOrder = Math.max(...categories.map(c => c.order), 0) + 1;
        await createCategory({
          ...formData,
          order: nextOrder
        });
      }

      setIsModalOpen(false);
      await fetchCategories();
      setError(null);
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory(id);
      setCategories(categories.filter(category => category.id !== id));
      setDeleteConfirmationId(null);
      setError(null);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category. Please try again.');
    }
  };

  const handleToggleActive = async (category: Category) => {
    try {
      await updateCategory(category.id!, { isActive: !category.isActive });
      await fetchCategories();
      setError(null);
    } catch (err) {
      console.error('Error updating category:', err);
      setError('Failed to update category. Please try again.');
    }
  };

  const handleSeedCategories = async () => {
    setSeeding(true);
    setError(null); // Clear previous errors
    
    try {
      console.log('🌱 Starting seed process...');
      console.log('🔐 Current auth state:', auth.currentUser ? 'Authenticated' : 'Not authenticated');
      
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please log in again.');
      }
      
      console.log('📧 User email:', auth.currentUser.email);
      console.log('🔑 User UID:', auth.currentUser.uid);
      
      await seedDefaultCategories();
      await fetchCategories();
      console.log('✅ Seeding completed successfully');
      setError(null);
    } catch (err) {
      console.error('❌ Seeding failed:', err);
      
      // Extract more detailed error information
      let errorMessage = 'Failed to seed default categories.';
      
      if (err instanceof Error) {
        console.error('Error name:', err.name);
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        
        // Check for specific Firebase errors
        if (err.message.includes('permission-denied')) {
          errorMessage = 'Permission denied. Please check if you have admin privileges.';
        } else if (err.message.includes('network-request-failed')) {
          errorMessage = 'Network error. Please check your internet connection.';
        } else if (err.message.includes('auth')) {
          errorMessage = 'Authentication error. Please log out and log in again.';
        } else {
          errorMessage = `Failed to seed categories: ${err.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setSeeding(false);
    }
  };

  // Test function to create a single category
  const handleTestCategory = async () => {
    try {
      console.log('🧪 Testing category creation...');
      console.log('🔐 Auth user:', auth.currentUser?.email);
      
      const testCategory = {
        name: 'Test Category',
        description: 'This is a test category',
        color: '#FF5733',
        order: 999,
        isActive: true
      };
      
      const categoryId = await createCategory(testCategory);
      console.log('✅ Test category created with ID:', categoryId);
      
      await fetchCategories();
      setError(null);
    } catch (err) {
      console.error('❌ Test category creation failed:', err);
      setError(`Test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Test Firestore connection and permissions
  const handleTestConnection = async () => {
    try {
      console.log('🔗 Testing Firestore connection...');
      console.log('🔐 Current user:', auth.currentUser?.email);
      
      // Test read permission
      console.log('📖 Testing read permission...');
      const existingCategories = await getAllCategories();
      console.log('✅ Read permission works. Found categories:', existingCategories.length);
      
      setError(`Connection test passed! User: ${auth.currentUser?.email}, Categories: ${existingCategories.length}`);
    } catch (err) {
      console.error('❌ Connection test failed:', err);
      setError(`Connection test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Reordering functionality - ready for drag & drop implementation
  // const moveCategory = async (fromIndex: number, toIndex: number) => {
  //   const newCategories = [...categories];
  //   const [movedCategory] = newCategories.splice(fromIndex, 1);
  //   newCategories.splice(toIndex, 0, movedCategory);

  //   // Update local state immediately for better UX
  //   setCategories(newCategories);

  //   try {
  //     // Update order values
  //     const reorderData = newCategories.map((category, index) => ({
  //       id: category.id!,
  //       order: index + 1
  //     }));

  //     await reorderCategories(reorderData);
  //     setError(null);
  //   } catch (err) {
  //     console.error('Error reordering categories:', err);
  //     setError('Failed to reorder categories. Please try again.');
  //     // Revert on error
  //     await fetchCategories();
  //   }
  // };

  return (
    <div className="container py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Categories Manager</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage project categories for filtering and organization
          </p>
        </div>
        <div className="flex gap-3">
          {categories.length === 0 && (
            <>
              <button
                onClick={handleTestConnection}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center"
              >
                Test Connection
              </button>
              <button
                onClick={handleTestCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                Test Creation
              </button>
              <button
                onClick={handleSeedCategories}
                disabled={seeding}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
              >
                {seeding ? (
                  <>
                    <div className="animate-spin mr-2 h-4 w-4 border-b-2 border-white rounded-full"></div>
                    Seeding...
                  </>
                ) : (
                  'Seed Default Categories'
                )}
              </button>
            </>
          )}
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex justify-between items-start">
          <span className="flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-4 text-red-700 hover:text-red-900 font-bold"
            title="Clear error"
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {categories.length === 0 ? (
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                  No categories found. Test the connection first, then create your first category or seed with defaults.
                </p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <button
                    onClick={handleTestConnection}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    Test Connection
                  </button>
                  <button
                    onClick={handleTestCategory}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Test Creation
                  </button>
                  <button
                    onClick={handleSeedCategories}
                    disabled={seeding}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    {seeding ? 'Seeding...' : 'Seed Default Categories'}
                  </button>
                  <button
                    onClick={handleCreateNew}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Create First Category
                  </button>
                </div>
              </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-4">
                      <button
                        className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600"
                        onMouseDown={() => {/* Drag implementation can be added here */}}
                      >
                        <Bars3Icon className="h-5 w-5" />
                      </button>
                      
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      
                      <div>
                        <h3 className="text-lg font-semibold flex items-center">
                          {category.name}
                          {!category.isActive && (
                            <span className="ml-2 px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                              Inactive
                            </span>
                          )}
                        </h3>
                        {category.description && (
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(category)}
                        className={`p-2 rounded-md transition-colors ${
                          category.isActive
                            ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20'
                            : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={category.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {category.isActive ? (
                          <EyeIcon className="h-5 w-5" />
                        ) : (
                          <EyeSlashIcon className="h-5 w-5" />
                        )}
                      </button>

                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>

                      {deleteConfirmationId === category.id ? (
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleDelete(category.id!)}
                            className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-md transition-colors"
                            title="Confirm Delete"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmationId(null)}
                            className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            title="Cancel"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmationId(category.id!)}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </>
      )}

      {/* Modal for creating/editing categories */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">
                    {editingCategory ? 'Edit Category' : 'Create New Category'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="e.g., Web Application"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="Brief description of this category"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                        placeholder="#3B82F6"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Active (visible to users)
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      {editingCategory ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoriesEditor; 